import classNames from "classnames";
import { ReactNode, useCallback, useEffect, useRef, useState } from "react";

import { MenuContext, MenuContextValue } from "./MenuContext";

import "./Menu.css";

export type MenuProps = {
  className?: string;
  children: ReactNode;
};

const Menu = ({ className, children }: MenuProps) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  const close = useCallback(() => setOpen(false), []);
  const toggle = useCallback(() => setOpen((o) => !o), []);

  useEffect(() => {
    if (!open) return;

    const handler = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node;
      if (
        menuRef.current?.contains(target) ||
        triggerRef.current?.contains(target)
      )
        return;
      close();
    };

    document.addEventListener("mousedown", handler);
    document.addEventListener("touchstart", handler);

    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("touchstart", handler);
    };
  }, [open, close]);

  useEffect(() => {
    if (!open) return;

    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        close();
        triggerRef.current?.focus();
      }
    };

    document.addEventListener("keydown", handler);

    return () => document.removeEventListener("keydown", handler);
  }, [open, close]);

  const value: MenuContextValue = {
    open,
    toggle,
    close,
    triggerRef,
    menuRef,
  };

  return (
    <MenuContext.Provider value={value}>
      <div className={classNames("Menu", className)}>{children}</div>
    </MenuContext.Provider>
  );
};

export default Menu;
